import type { PropsWithChildren, FC } from "react";
import { useEffect } from "react";
import { userContext } from "./userContext";
import type { User, ProfileResponse } from "../../types/user";
import { useCustomQuery, useCustomMutation } from "../../utils/api/apiService";
import { ENDPOINTS } from "../../utils/api/endpoints";
import type { BaseResponse } from "../../types/api";
import TomanAnimatedLogo from "../../components/TomanAnimatedLogo";
import { useKeycloak } from "../keycloak/keycloakContext";
import { getDeviceInfo } from "../../utils/device";
import { mapDeviceInfoToSessionRequest } from "../../types/session";
import type { SessionRequest } from "../../types/session";
import { setSentryUser, addBreadcrumb, captureException } from "../../utils/sentry/sentryConfig";

const SESSION_CALLED_TOKEN_KEY = "session_called_refresh_token";

const UserProvider: FC<PropsWithChildren> = ({ children }) => {
  const { authState, keycloakService } = useKeycloak();

  const { data: profile, refetch: refetchProfile } = useCustomQuery<BaseResponse<ProfileResponse>>(
    ENDPOINTS.profile.profile,
    { enabled: authState.isAuthenticated },
  );

  const { mutate: createSession } = useCustomMutation<SessionRequest, void>(
    ENDPOINTS.users.sessions,
    {
      showToast: false,
    },
  );

  useEffect(() => {
    if (authState.isAuthenticated && profile?.data?.user && authState.refreshToken) {
      const storedRefreshToken = localStorage.getItem(SESSION_CALLED_TOKEN_KEY);

      if (storedRefreshToken !== authState.refreshToken) {
        localStorage.setItem(SESSION_CALLED_TOKEN_KEY, authState.refreshToken);
        getDeviceInfo()
          .then((deviceInfo) => {
            const sessionData = mapDeviceInfoToSessionRequest(deviceInfo);
            addBreadcrumb("Creating user session", "session", "info");
            createSession(sessionData);
          })
          .catch((error) => {
            addBreadcrumb("Failed to get device info for session", "session", "error", {
              error: error instanceof Error ? error.message : String(error),
            });
            captureException(error, {
              tags: { error_type: "device_info_failed" },
              level: "warning",
            });
          });
      }
    }
  }, [authState.isAuthenticated, authState.refreshToken, profile?.data?.user, createSession]);

  useEffect(() => {
    if (!authState.isAuthenticated) {
      localStorage.removeItem(SESSION_CALLED_TOKEN_KEY);
      setSentryUser(null);
      addBreadcrumb("User logged out - cleared Sentry user context", "auth", "info");
    }
  }, [authState.isAuthenticated]);

  useEffect(() => {
    if (profile?.data?.user) {
      const user = profile.data.user;
      setSentryUser({
        id: user.id,
        username: user.code,
        email: user.phone_number,
      });
      addBreadcrumb("User profile loaded - set Sentry user context", "user", "info", {
        user_id: user.id,
        user_code: user.code,
      });
    }
  }, [profile?.data?.user]);

  const handleLogout = async () => {
    localStorage.removeItem(SESSION_CALLED_TOKEN_KEY);
    await keycloakService.logout();
  };

  const user = profile?.data?.user;

  return (
    <userContext.Provider
      value={{
        user: user as User,
        token: authState.token || "",
        refresh_token: authState.refreshToken || "",
        logout: handleLogout,
        isLoggingOut: false,
        refetchProfile,
      }}
    >
      {!user ? <TomanAnimatedLogo /> : children}
    </userContext.Provider>
  );
};

export default UserProvider;
